import { Align, ITableColumn, Justify, Label, Row, Table, TextAlign } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { EmptyState, HeaderDialog, Spinner } from 'components';
import { useOpenable } from 'hooks';
import { find, isNil, sumBy, toNumber } from 'lodash';
import { ReactNode, useState } from 'react';
import { ClassName } from 'types/ClassName';
import IPosition from 'types/Position';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import PositionDetails from '../../PositionDetails';
import './PositionDetailsTable.scss';
import usePositionDetailsTableData, { IPositionDetailsTableRow } from './hooks/usePositionDetailsTableData';

export interface IPositionDetailsTableProps {
  className?: ClassName;
  scroller?: HTMLElement | Document;
}

const PositionDetailsTable = (props: IPositionDetailsTableProps) => {
  const { className, scroller } = props;

  const { getRows, getPositionValue, refetch, positions, loading } = usePositionDetailsTableData();
  const isProgressEnabled = useFeatureFlags('WORK_ACCOMPLISHED');
  const [selectedPosition, setSelectedPosition] = useState<IPosition | null>(null);
  const positionDetailsOpenable = useOpenable();

  function getColumns(): Array<ITableColumn<IPositionDetailsTableRow>> {
    const columns: Array<ITableColumn<IPositionDetailsTableRow>> = [
      {
        cell: renderRow,
        key: 'permission',
        sort: false,
        title: '',
        align: TextAlign.LEFT,
        size: '200px',
        textWrap: true,
      },
    ];

    positions.forEach((position) => {
      columns.push({
        cell: renderRow,
        key: position.id,
        sort: false,
        title: position.title,
        align: TextAlign.LEFT,
        size: '200px',
        textWrap: true,
      });
    });

    return columns;
  }

  function renderRow(row: IPositionDetailsTableRow, col: ITableColumn<IPositionDetailsTableRow>): ReactNode {
    switch (col.key) {
      case 'permission':
        return <Label>{row.permissionText}</Label>;
      default:
        if (row.positionKey === 'editButton') {
          const position = row.positions.find((p) => p.id === col.key);
          return (
            <div
              className={'edit-button'}
              onClick={() => {
                if (!isNil(position)) {
                  setSelectedPosition(position);
                  positionDetailsOpenable.open();
                }
              }}
            >
              {position?.level === 1 ? t('View') : t('Edit Position')}
            </div>
          );
        }
        return <>{getPositionValue(row, col.key)}</>;
    }
  }

  const tableWidth = () => {
    const columns = getColumns();
    return sumBy(columns, (column) => toNumber(column.size?.replace('px', '') ?? '0')) + 300;
  };

  const renderEmptyState = () => {
    return <EmptyState title={t('No Positions')} />;
  };

  async function handleUpdate() {
    return refetch().then((updatedPositions) => {
      if (selectedPosition) {
        const updatedPosition = find(updatedPositions.data.positions, { id: selectedPosition.id });

        if (updatedPosition) {
          setSelectedPosition(updatedPosition);
        }
      } else {
        setSelectedPosition(null);
      }
    });
  }

  const classes = classNames('position-details-table', className);

  return (
    <div className={classes}>
      <Table
        cols={getColumns()}
        data={getRows()}
        minWidth={`${tableWidth()}px`}
        lazyLoad={false}
        strokeCols={true}
        header="standard"
        scroller={scroller}
        emptyTemplate={!loading && renderEmptyState()}
      />
      {loading && (
        <Row justify={Justify.CENTER} align={Align.CENTER} className="py-8">
          <Spinner />
        </Row>
      )}
      <div className="footer-label">
        <Label className="py-8 pl-4">{'* ' + t('Pro feature only.')}</Label>
        {isProgressEnabled && <Label className="py-8 pl-4">{'** ' + t('Add-on pricing required.')}</Label>}
      </div>
      <HeaderDialog
        title={selectedPosition?.title ?? ''}
        size="full"
        isOpen={positionDetailsOpenable.isOpen}
        hasDismiss={true}
        onClose={positionDetailsOpenable.close}
      >
        {selectedPosition && (
          <PositionDetails
            position={selectedPosition}
            onUpdate={handleUpdate}
            onClose={positionDetailsOpenable.close}
          />
        )}
      </HeaderDialog>
    </div>
  );
};

export default PositionDetailsTable;
